import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * Tiny theme provider — persists the chosen mode in localStorage and
 * toggles a `dark` class on document.body. All visual flips live in
 * /app/frontend/src/dark.css; this context only owns the on/off bit.
 *
 * Why body.dark and not html.dark?
 *   We don't use Tailwind's `dark:` variant (that would force a refactor
 *   of every hardcoded brand color across the app). Instead, dark.css
 *   uses `body.dark [class*="bg-[#FDFBF7]"]` etc. so dark mode is purely
 *   additive — switching back to light removes all overrides instantly.
 */

const STORAGE_KEY = "qfresh_theme";
const ThemeContext = createContext({
  theme: "light",
  setTheme: () => {},
  toggleTheme: () => {},
});

const readStoredTheme = () => {
  if (typeof window === "undefined") return "light";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    // localStorage can throw in private browsing — fall through to default.
  }
  // First-visit default: LIGHT. Users who want dark can toggle it explicitly
  // via the Settings page; that choice is remembered in localStorage.
  // (We intentionally do NOT respect `prefers-color-scheme: dark` here —
  // most phones auto-switch to dark at night, and users reported being
  // unexpectedly logged into the app in night mode.)
  return "light";
};

const applyTheme = (theme) => {
  if (typeof document === "undefined") return;
  document.body.classList.toggle("dark", theme === "dark");
  // Sync the meta color-scheme so the browser chrome (form controls,
  // scrollbars on some platforms) matches the chosen theme.
  document.documentElement.style.colorScheme = theme;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(readStoredTheme);

  // Apply on mount + whenever the value changes.
  useEffect(() => {
    applyTheme(theme);
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // ignore storage errors
    }
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (next !== "light" && next !== "dark") return;
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((t) => (t === "dark" ? "light" : "dark"));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
